// ============================================================================
// Funcionalidades:
// - Buscar pacientes por nome ou CPF
// - Listar pacientes em DataTable
// - Selecionar paciente para usar no agendamento
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState, useEffect } from "react";
import { useAuth } from "../../AuthContext";
import { FaUser, FaIdCard, FaSearch } from "react-icons/fa";

const DialogPesquisarPaciente = ({
  // Controle do diálogo
  visible,
  onHide,

  // Callback para quando um paciente for selecionado
  onPacienteSelecionado,

  // Funções para mostrar mensagens (opcional)
  showErrorMessage,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pacientes, setPacientes] = useState([]);
  const [pacientesFiltrados, setPacientesFiltrados] = useState([]);
  const [pesquisarNome, setPesquisarNome] = useState("");
  const [pesquisarCpf, setPesquisarCpf] = useState("");
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // FUNÇÃO: Buscar pacientes da API
  // ==========================================================================
  const fetchPacientes = async () => {
    setLoading(true);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pacientes",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar pacientes");
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        setPacientes(data.data);
        setPacientesFiltrados(data.data);
      } else {
        setPacientes([]);
        setPacientesFiltrados([]);
        if (showErrorMessage) {
          showErrorMessage("Nenhum paciente encontrado.");
        }
      }
    } catch (error) {
      console.error("❌ Erro ao buscar pacientes:", error);
      setPacientes([]);
      setPacientesFiltrados([]);
      if (showErrorMessage) {
        showErrorMessage(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // EFFECT: Abrir o diálogo e carregar pacientes
  // ==========================================================================
  useEffect(() => {
    if (visible) {
      // Limpar campos anteriores
      setPesquisarNome("");
      setPesquisarCpf("");
      setPacienteSelecionado(null);
      // Buscar pacientes
      fetchPacientes();
    }
  }, [visible]);

  // ==========================================================================
  // EFFECT: Filtrar pacientes por nome e CPF
  // ==========================================================================
  useEffect(() => {
    if (pacientes.length > 0) {
      let filtrados = [...pacientes];

      if (pesquisarNome.trim() !== "") {
        filtrados = filtrados.filter((paciente) =>
          paciente.nome?.toLowerCase().includes(pesquisarNome.toLowerCase()),
        );
      }

      if (pesquisarCpf.trim() !== "") {
        const cpfPesquisa = pesquisarCpf.replace(/[^\d]/g, "");
        filtrados = filtrados.filter((paciente) => {
          const cpfPaciente = paciente.cpf?.replace(/[^\d]/g, "") || "";
          return cpfPaciente.includes(cpfPesquisa);
        });
      }

      setPacientesFiltrados(filtrados);
    } else {
      setPacientesFiltrados([]);
    }
  }, [pesquisarNome, pesquisarCpf, pacientes]);

  // ==========================================================================
  // HANDLER: Selecionar paciente
  // ==========================================================================
  const handleSelecionarPaciente = () => {
    if (pacienteSelecionado) {
      // Chamar o callback do pai com os dados do paciente
      if (onPacienteSelecionado) {
        onPacienteSelecionado(pacienteSelecionado);
      }
      // Fechar o diálogo
      onHide();
    }
  };

  // ==========================================================================
  // FUNÇÃO: Cancelar - Fecha o diálogo
  // ==========================================================================
  const handleCancelar = () => {
    setPesquisarNome("");
    setPesquisarCpf("");
    setPacienteSelecionado(null);
    onHide();
  };

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const inputStyle = {
    width: "100%",
    height: "40px",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "0 12px",
    transition: "all 0.2s",
    outline: "none",
  };

  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <Dialog
      visible={visible}
      modal={true}
      blockScroll={true}
      style={{
        width: "90vw",
        maxWidth: "620px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={handleCancelar}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaSearch style={{ fontSize: "2.4rem", color: "#B83732" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                PESQUISAR
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                PACIENTE
              </div>
            </div>
          </div>
        </div>
      }
    >
      <>
        {/* CARD ÚNICO */}
        <div
          style={{
            backgroundColor: "#FAFAFA",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          {/* GRID RESPONSIVO: Desktop 2 colunas, Mobile 1 coluna */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            {/* Nome */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaUser style={iconStyle} />
                <span>NOME</span>
              </div>
              <InputText
                value={pesquisarNome}
                onChange={(e) => setPesquisarNome(e.target.value)}
                placeholder="Pesquisar por nome"
                style={inputStyle}
              />
            </div>

            {/* CPF */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaIdCard style={iconStyle} />
                <span>CPF</span>
              </div>
              <InputText
                value={pesquisarCpf}
                onChange={(e) => setPesquisarCpf(e.target.value)}
                placeholder="000.000.000-00"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Tabela de Resultados */}
          <div
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              marginBottom: "20px",
              maxHeight: "320px",
              overflowY: "auto",
            }}
          >
            <DataTable
              value={pacientesFiltrados}
              stripedRows
              paginator
              rows={5}
              rowsPerPageOptions={[]}
              paginatorTemplate={isMobile ? "PrevPageLink PageLinks NextPageLink" : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"}
              pageLinkSize={isMobile ? 2 : 4}
              selectionMode="single"
              selection={pacienteSelecionado}
              onSelectionChange={(e) => setPacienteSelecionado(e.value)}
              dataKey="id"
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: "0.9rem",
                minWidth: "300px",
              }}
              emptyMessage={
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#95A5A6",
                  }}
                >
                  {loading ? "Carregando..." : "Nenhum paciente encontrado"}
                </div>
              }
              size="small"
            >
              <Column
                selectionMode="single"
                headerStyle={{ width: "3rem" }}
                style={{ textAlign: "center" }}
              />
              <Column
                field="nome"
                header="Nome"
                style={{ color: "#2C3E50" }}
                body={(rowData) => (
                  <span
                    style={{
                      fontWeight:
                        pacienteSelecionado?.id === rowData.id
                          ? "bold"
                          : "normal",
                    }}
                  >
                    {rowData.nome}
                  </span>
                )}
              />
              <Column
                field="cpf"
                header="CPF"
                style={{ color: "#2C3E50" }}
                body={(rowData) => <span>{rowData.cpf || "---"}</span>}
              />
            </DataTable>
          </div>

          {/* ========== BOTÕES DE AÇÃO ========== */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <Button
              label="CANCELAR"
              icon="pi pi-times"
              onClick={handleCancelar}
              className="p-button-outlined"
              style={{
                padding: "10px 28px",
                minWidth: "120px",
                borderColor: "#e2e8f0",
                color: "#718096",
                fontWeight: "600",
                borderRadius: "8px",
              }}
              disabled={loading}
            />
            <Button
              label={loading ? "SELECIONANDO..." : "SELECIONAR"}
              icon="pi pi-check"
              onClick={handleSelecionarPaciente}
              className="p-button-success p-button-sm"
              style={{
                padding: "10px 28px",
                minWidth: "160px",
                fontWeight: "600",
                borderRadius: "8px",
                color: "white",
              }}
              disabled={!pacienteSelecionado || loading}
              loading={loading}
            />
          </div>
        </div>
      </>
    </Dialog>
  );
};

export default DialogPesquisarPaciente;
